/*
 * A small markdown-to-HTML renderer for assistant replies.
 *
 * Why not react-markdown / marked: this app is on react-scripts 4, i.e.
 * webpack 4. react-markdown >= 7 and marked >= 5 are ESM-only and fail to
 * bundle here; the last CJS versions are old enough to be their own liability.
 * The subset the model actually emits — headings, lists, tables, code, bold,
 * links — is small enough to render directly.
 *
 * Security: everything is HTML-escaped FIRST, and markup is only ever
 * introduced by this file. That matters more than usual here, because replies
 * quote untrusted text (post captions, creator bios) that reached the model
 * through the tools. Nothing coming from the model can inject a tag or an
 * attribute. Link hrefs are additionally restricted to http/https/mailto/root
 * paths, so a `[click](javascript:...)` in a caption cannot become a live link.
 */

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const SAFE_URL = /^(https?:\/\/|mailto:|\/)/i;

// Inline formatting. Runs on ALREADY-ESCAPED text, so the only angle brackets
// present are the ones added below.
const inline = (text) => {
  let out = text;

  // Code spans first — their contents must not be re-processed for emphasis.
  const codeSpans = [];
  out = out.replace(/`([^`]+)`/g, (_match, code) => {
    codeSpans.push(code);
    return `@@CODE${codeSpans.length - 1}@@`;
  });

  // The href pattern allows one level of balanced parentheses, so both a
  // Wikipedia-style URL and a `javascript:alert(1)` are captured WHOLE. A
  // partial capture would leave the closing paren as visible junk and, worse,
  // let a dangerous scheme through under a truncated href.
  out = out.replace(/\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/g, (_match, label, href) => {
    // href arrives escaped, so &amp; must be undone before validating.
    const url = href.replace(/&amp;/g, '&');
    if (!SAFE_URL.test(url)) return label;
    return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });

  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  out = out.replace(/~~([^~]+)~~/g, '<del>$1</del>');

  out = out.replace(/@@CODE(\d+)@@/g, (_m, i) => `<code>${codeSpans[Number(i)]}</code>`);
  return out;
};

const isTableDivider = (line) =>
  /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes('-');

const splitRow = (line) =>
  line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((cell) => cell.trim());

// Undo the escaping done above. CSV is a file, not HTML — writing "&amp;" into
// a spreadsheet cell would be a bug.
const unescapeHtml = (text) =>
  String(text)
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

// Strip the markdown a cell may carry, so the CSV holds values rather than
// formatting: **bold**, *em*, `code`, and [label](url) -> label.
const plainCell = (cell) =>
  unescapeHtml(cell)
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();

/*
 * Quote one CSV field.
 *
 * The leading-character guard is not cosmetic. These exports contain creator
 * bios and post captions — text we did not write — and Excel/Sheets treat a
 * cell starting with = + - @ as a formula. A crafted bio could therefore run
 * on open. Prefixing with an apostrophe is the standard mitigation: the cell
 * displays as text and the value is preserved.
 */
const csvField = (value) => {
  let field = String(value == null ? '' : value);
  // = + - and leading whitespace control chars always start a formula.
  // '@' is deliberately NOT in that set: almost every row here begins with a
  // social handle, and prefixing those would corrupt the common case to guard
  // the rare one. '@' is escaped only when the value also looks like a call or
  // a DDE payload, which a handle never does.
  // A plain number is never a formula, and these exports are full of negative
  // deltas — escaping "-5" would turn a number into text in every spreadsheet.
  const isNumeric = /^[-+]?\d[\d,]*(\.\d+)?%?$/.test(field);
  const formulaLead = !isNumeric && /^[=+\-\t\r]/.test(field);
  const suspiciousAt = field.startsWith('@') && /[(|]/.test(field);
  if (formulaLead || suspiciousAt) field = `'${field}`;
  if (/[",\n\r]/.test(field)) field = `"${field.replace(/"/g, '""')}"`;
  return field;
};

const toCsv = (rows) => rows.map((row) => row.map(csvField).join(',')).join('\r\n');

const safeFilename = (name, fallback) => {
  const cleaned = String(name || '')
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  if (!cleaned) return fallback;
  return /\.csv$/i.test(cleaned) ? cleaned : `${cleaned}.csv`;
};

// Split a CSV line, honouring quoted fields containing commas.
const parseCsvLine = (line) => {
  const out = [];
  let field = '';
  let quoted = false;
  for (let c = 0; c < line.length; c += 1) {
    const ch = line[c];
    if (quoted) {
      if (ch === '"' && line[c + 1] === '"') { field += '"'; c += 1; }
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { out.push(field); field = ''; }
    else field += ch;
  }
  out.push(field);
  return out.map((f) => f.trim());
};

/*
 * Returns { html, downloads }.
 *
 * `downloads` carries the CSV payloads the HTML refers to by
 * data-download="<id>"; Markdown.jsx turns those into real file saves. The
 * renderer stays a pure string function — it never touches the DOM.
 */
export const renderMarkdown = (source) => {
  const lines = escapeHtml(source || '').split('\n');
  const html = [];
  const downloads = [];
  const listStack = [];
  let i = 0;

  const addDownload = (filename, rows) => {
    const id = `dl${downloads.length}`;
    downloads.push({
      id,
      filename,
      csv: toCsv(rows),
      rows: Math.max(rows.length - 1, 0),
      columns: rows.length ? rows[0].length : 0,
    });
    return downloads[downloads.length - 1];
  };

  const downloadBar = (dl, label) =>
    `<div class="md-dl"><span class="md-dl-meta">${escapeHtml(dl.filename)}` +
    ` · ${dl.rows} row${dl.rows === 1 ? '' : 's'}</span>` +
    `<button type="button" class="md-dl-btn" data-download="${dl.id}">${label}</button></div>`;

  const closeLists = () => {
    while (listStack.length) html.push(`</${listStack.pop()}>`);
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    const fence = line.match(/^\s*```\s*(\S*)\s*(.*)$/);
    if (fence) {
      closeLists();
      const body = [];
      i += 1;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // consume the closing fence

      // A ```csv fence is data, not code. Render it as a file the reader can
      // save, with the first rows previewed — dumping hundreds of comma-
      // separated lines into the transcript is what this replaces.
      if (/^csv$/i.test(fence[1] || '')) {
        const dataRows = body
          .map((row) => row.trim())
          .filter(Boolean)
          .map((row) => parseCsvLine(unescapeHtml(row)));
        if (dataRows.length) {
          const dl = addDownload(
            safeFilename(fence[2], 'export.csv'),
            dataRows
          );
          const head = (dataRows[0] || [])
            .map((c) => `<th>${escapeHtml(c)}</th>`)
            .join('');
          const preview = dataRows
            .slice(1, 21)
            .map(
              (row) =>
                `<tr>${row.map((c) => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`
            )
            .join('');
          const more =
            dataRows.length > 21
              ? `<div class="md-dl-more">Showing 20 of ${dataRows.length - 1} rows` +
                ` — download for the full file.</div>`
              : '';
          html.push(
            `<div class="md-file">${downloadBar(dl, 'Download CSV')}` +
              `<div class="md-table-wrap"><table><thead><tr>${head}</tr></thead>` +
              `<tbody>${preview}</tbody></table></div>${more}</div>`
          );
          continue;
        }
      }

      html.push(`<pre><code>${body.join('\n')}</code></pre>`);
      continue;
    }

    // GFM table: a header row followed by a divider row
    if (line.includes('|') && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
      closeLists();
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      const head = header.map((c) => `<th>${inline(c)}</th>`).join('');
      const body = rows
        .map((row) => `<tr>${row.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`)
        .join('');
      // Any table worth reading is worth exporting; the model answers with a
      // markdown table far more often than with an explicit CSV fence.
      const dl = addDownload(
        'export.csv',
        [header.map(plainCell), ...rows.map((row) => row.map(plainCell))]
      );
      html.push(
        `<div class="md-file">${downloadBar(dl, 'Download CSV')}` +
          `<div class="md-table-wrap"><table><thead><tr>${head}</tr></thead>` +
          `<tbody>${body}</tbody></table></div></div>`
      );
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      closeLists();
      // "#" renders as h3 — h1/h2 belong to the page, not to a reply.
      const level = Math.min(heading[1].length + 2, 6);
      html.push(`<h${level}>${inline(heading[2])}</h${level}>`);
      i += 1;
      continue;
    }

    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      closeLists();
      html.push('<hr />');
      i += 1;
      continue;
    }

    // "&gt;" because the source was escaped above.
    const quote = line.match(/^\s*&gt;\s?(.*)$/);
    if (quote) {
      closeLists();
      html.push(`<blockquote>${inline(quote[1])}</blockquote>`);
      i += 1;
      continue;
    }

    const bullet = line.match(/^\s*[-*+]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      const tag = bullet ? 'ul' : 'ol';
      if (!listStack.length || listStack[listStack.length - 1] !== tag) {
        closeLists();
        html.push(`<${tag}>`);
        listStack.push(tag);
      }
      html.push(`<li>${inline((bullet || numbered)[1])}</li>`);
      i += 1;
      continue;
    }

    if (!line.trim()) {
      closeLists();
      i += 1;
      continue;
    }

    // Paragraph: gather until a blank line or the start of another block.
    closeLists();
    const paragraph = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^\s*```/.test(lines[i]) &&
      !/^#{1,4}\s+/.test(lines[i]) &&
      !/^\s*[-*+]\s+/.test(lines[i]) &&
      !/^\s*\d+[.)]\s+/.test(lines[i])
    ) {
      paragraph.push(lines[i]);
      i += 1;
    }
    html.push(`<p>${inline(paragraph.join('<br />'))}</p>`);
  }

  closeLists();
  return { html: html.join('\n'), downloads };
};

export default renderMarkdown;
